/**
 * 전역 셀 합계 선택 — 엑셀처럼 표의 숫자 셀을 다중 선택하면 합계를 말풍선으로 표시.
 *
 * App 루트에 한 번만 mount → 모든 표(.num 숫자 셀)에 자동 적용. 표별 변경 불필요.
 *
 * 대상: `<td>` 안의 `.num` 요소(표 본문의 숫자 셀). 입력 폼의 .num input 은 td 밖이라 제외.
 *
 * 선택 방식 (행 클릭=상세 열기와 충돌 안 나게):
 *   - 드래그: 숫자 셀 위를 마우스로 끌면 지나간 셀들이 선택됨(범위/페인트).
 *   - Ctrl/⌘ + 클릭: 셀을 하나씩 토글 추가/해제.
 *   - 평클릭(수정키·드래그 없음): 선택 해제 + 기존 행 클릭(상세 열기)은 그대로 동작.
 *   - Esc / 빈 곳 클릭: 선택 해제.
 *
 * 말풍선: 2칸 이상 선택 시 합계 / 개수 / 평균 표시. (마우스 위치 근처, 화면 안쪽 clamp)
 *
 * 값 파싱: 셀 텍스트에서 숫자(콤마/₩/원 등 제거)만 추출. '***'(viewer 마스킹)·'·'·'-' 등은 제외.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface BubbleState {
  sum: number;
  count: number;
  x: number;
  y: number;
}

const SEL_CLASS = 'cellsum-sel';

/** 숫자(.num)가 들어있는 표 본문 칸(<td>) 을 반환. 칸 어디를 눌러도 선택되게 td 단위로 잡음. */
function numElement(td: HTMLElement): HTMLElement | null {
  const numEl = (td.matches('.num') ? td : td.querySelector('.num')) as HTMLElement | null;
  if (!numEl) return null;
  if (numEl.tagName === 'INPUT' || numEl.tagName === 'TEXTAREA' || numEl.tagName === 'SELECT') return null;
  return numEl;
}
function pickCell(t: EventTarget | null): HTMLElement | null {
  if (!(t instanceof HTMLElement)) return null;
  const td = t.closest('td') as HTMLElement | null;
  if (!td) return null; // 표 본문 칸만 (헤더·폼 입력 제외)
  if (!numElement(td)) return null; // 숫자 값이 든 칸만
  return td;
}
/** 칸의 숫자 값 (없으면 null). 칸 안 .num 텍스트에서 추출. */
function cellValue(td: HTMLElement): number | null {
  const numEl = numElement(td);
  return numEl ? parseNum(numEl.textContent) : null;
}

/** 셀 텍스트 → 숫자 (없으면 null). 콤마·통화기호·공백 제거. */
function parseNum(text: string | null): number | null {
  if (!text) return null;
  const cleaned = text.replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function CellSumSelectionMount() {
  const [bubble, setBubble] = useState<BubbleState | null>(null);

  useEffect(() => {
    const selected = new Map<HTMLElement, number>();
    let isDown = false;
    let dragMoved = false;
    let startEl: HTMLElement | null = null;
    let suppressClick = false;
    let downX = 0;
    let downY = 0;
    let lastX = 0;
    let lastY = 0;

    function recompute(x: number, y: number) {
      if (selected.size < 2) {
        setBubble(null);
        return;
      }
      let sum = 0;
      selected.forEach((v) => (sum += v));
      setBubble({ sum, count: selected.size, x, y });
    }

    function addCell(el: HTMLElement) {
      if (selected.has(el)) return;
      const v = cellValue(el);
      if (v === null) return; // 숫자 아님(마스킹/빈칸) → 무시
      selected.set(el, v);
      el.classList.add(SEL_CLASS);
    }

    function toggleCell(el: HTMLElement) {
      if (selected.has(el)) {
        selected.delete(el);
        el.classList.remove(SEL_CLASS);
      } else {
        addCell(el);
      }
    }

    function clearSelection() {
      selected.forEach((_v, el) => el.classList.remove(SEL_CLASS));
      selected.clear();
      setBubble(null);
    }

    function onMouseDown(e: MouseEvent) {
      if (e.button !== 0) return; // 좌클릭만
      const cell = pickCell(e.target);
      const mod = e.ctrlKey || e.metaKey;

      if (!cell) {
        // 셀·말풍선 밖 클릭 → 선택 해제
        if (!(e.target instanceof HTMLElement && e.target.closest('[data-cellsum-bubble]'))) {
          clearSelection();
        }
        return;
      }

      if (mod) {
        // Ctrl/⌘+클릭: 토글. 행 클릭(상세) 차단.
        e.preventDefault();
        e.stopPropagation();
        toggleCell(cell);
        suppressClick = true;
        recompute(e.clientX, e.clientY);
        return;
      }

      // 평범한 누름 → 드래그 가능성 (아직 평클릭일 수도). 텍스트선택은 이동 시 막음.
      isDown = true;
      dragMoved = false;
      startEl = cell;
      downX = e.clientX;
      downY = e.clientY;
      lastX = e.clientX;
      lastY = e.clientY;
    }

    function onMouseMove(e: MouseEvent) {
      if (!isDown) return;
      lastX = e.clientX;
      lastY = e.clientY;
      if (!dragMoved) {
        // 임계값 넘으면 드래그 선택 시작
        if (Math.abs(e.clientX - downX) < 4 && Math.abs(e.clientY - downY) < 4) return;
        dragMoved = true;
        clearSelection(); // 드래그는 새 선택 (Ctrl 누적은 클릭으로)
        if (startEl) addCell(startEl);
      }
      e.preventDefault();
      window.getSelection()?.removeAllRanges(); // 텍스트 선택 방지
      const cell = pickCell(e.target);
      if (cell) addCell(cell);
      recompute(e.clientX, e.clientY);
    }

    function onMouseUp(e: MouseEvent) {
      if (!isDown) return;
      isDown = false;
      if (dragMoved) {
        // 드래그 선택 끝 → 뒤따르는 click(행 열기) 막기
        suppressClick = true;
        recompute(e.clientX, e.clientY);
      } else {
        // 평클릭 → 선택 해제(행 클릭은 그대로 진행)
        clearSelection();
      }
      startEl = null;
    }

    function onClickCapture(e: MouseEvent) {
      if (suppressClick) {
        e.preventDefault();
        e.stopPropagation();
        suppressClick = false;
      }
    }

    function onKey(e: KeyboardEvent) {
      // 선택을 지우는 데 ESC 를 썼으면 창은 닫지 않는다 (v2.231.0 · H-4 · lib/dialog-stack.ts)
      if (e.key === 'Escape' && selected.size > 0) { e.preventDefault(); clearSelection(); }
    }

    function onScrollOrResize() {
      // 위치 어긋남 방지 — 선택 유지하되 말풍선은 마지막 위치 갱신
      if (selected.size >= 2) recompute(lastX, lastY);
    }

    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('mousemove', onMouseMove, true);
    document.addEventListener('mouseup', onMouseUp, true);
    document.addEventListener('click', onClickCapture, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      clearSelection();
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('mousemove', onMouseMove, true);
      document.removeEventListener('mouseup', onMouseUp, true);
      document.removeEventListener('click', onClickCapture, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, []);

  if (!bubble) return null;

  const avg = Math.round(bubble.sum / bubble.count);
  // 화면 안쪽으로 clamp
  const W = 220;
  const padding = 8;
  let x = bubble.x + 14;
  if (x + W > window.innerWidth - padding) x = window.innerWidth - W - padding;
  if (x < padding) x = padding;
  let y = bubble.y + 16;
  if (y > window.innerHeight - 70) y = bubble.y - 60;

  return createPortal(
    <div
      data-cellsum-bubble
      className="fixed z-[70] bg-slate-800 text-white px-3 py-2 rounded shadow-xl text-xs pointer-events-none"
      style={{ left: x, top: y, minWidth: W }}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-slate-300">합계</span>
        <span className="num font-bold text-sm">{bubble.sum.toLocaleString('ko-KR')}</span>
      </div>
      <div className="flex items-baseline justify-between gap-3 mt-0.5 text-xs text-slate-400">
        <span>{bubble.count}칸 선택 · 평균</span>
        <span className="num">{avg.toLocaleString('ko-KR')}</span>
      </div>
    </div>,
    document.body,
  );
}
